import '../styles/globals.css';
import '@zillow/constellation-fonts/zillow-fonts.css';
import 'core-js/actual/string/replace-all';

import type { ReactElement, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { CookiesProvider } from 'react-cookie';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { ToastProvider } from '@zillow/constellation-10';
import MobileDetect from 'mobile-detect';
import { setClient } from '@zg-rentals/ts-satellite-client/client';
import { useComscorePageTracking } from '@zg-rentals/comscore';
import { useZrmPageViewEvents } from '@zg-rentals/clickstream';
import { Head } from '@zg-rentals/next/pages';
import { DefaultQueryProvider } from '@zg-rentals/react-query5';
import { RpErrorBoundary, useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { useSiftScience } from '@zg-rentals/sift-science';
import { ERROR_DETAILS, ERROR_MESSAGE } from '@zg-rentals/satellite-components';
import { ErrorPage } from '@zg-rentals/error-page-components';
import { AppContextProvider } from '../contexts/AppContextProvider';
import { WebSocketProvider } from '@zg-rentals/web-socket-react';
import { getTopicSuffixes } from '@zg-rentals/satellite-web-socket-helpers';
import { useWebsocketFeature } from '../hooks/useWebsocketFeature';
import Auth, { useAuth } from '../AuthProvider';
import { bootstrap } from '../bootstrap';
import type { User } from '../AuthProvider';
import pathsManifest from '../utils/pathsManifest';
import StringConstants from '../constants/StringConstants';
import useWindowSize from '../../src/hooks/useWindowSize';
import { useRegisterNotification } from '@zg-rentals/register-notification';
import { rentalClient } from '../httpClient';
import { analytics, trackComscoreEvent, trackEvent } from '../analytics';
import { getBrand, isBrand } from '@zg-rentals/multibrand-support';
import type { Brand } from '@zg-rentals/multibrand-support';
import getIsNative from '../utils/getIsNative';
import Custom500 from './500';
import { ThemeConstellation } from '@zillow/constellation';
import { ThemeProvider } from 'styled-components';

export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

type UserAnalyticsProps = {
  user: User;
  brandName: Brand;
};

const UserAnalytics = ({ user, brandName }: UserAnalyticsProps) => {
  const router = useRouter();
  const { email, impersonatorUserToken, userToken } = user;

  useEffect(() => {
    if (brandName) {
      // only pass user token if brand is hotpads, otherwise rely on zillow cookie for encoded zuid
      if (brandName === 'hotpads') {
        analytics.initialize({ brand: brandName, userId: userToken || null });
      } else {
        analytics.initialize({ brand: brandName });
      }
    }
  }, [userToken, brandName]);

  // All analytics hooks
  useSiftScience({ email, isImpersonator: Boolean(impersonatorUserToken) });
  useZrmPageViewEvents(trackEvent, router.pathname, user);
  useComscorePageTracking(() => trackComscoreEvent({}), router.pathname);
  useRegisterNotification({ ref: 'SATELLITE_RACHEL_WEB' });

  return null;
};

setClient(rentalClient);

const getWebSocketUrl = (brand?: string): string => {
  if (process.env.NEXT_PUBLIC_WEBSOCKET_URL) {
    return process.env.NEXT_PUBLIC_WEBSOCKET_URL;
  }
  if (brand === 'zillow') {
    return 'wss://www.zillow.com/rental/satellite/ws/services';
  }
  return 'wss://hotpads.com/rental/satellite/ws/services';
};

/**
 * AppWithAuth handles user data fetching and app rendering.
 * Must be rendered inside DefaultQueryProvider to use React Query.
 */
const AppWithAuth = ({
  brandName,
  setBrandName,
  isMobileDevice,
  isMobileView,
  isInWebView,
  ...appProps
}: AppPropsWithLayout & {
  brandName: Brand;
  setBrandName: (brand: Brand | null) => void;
  isMobileDevice: boolean;
  isMobileView: boolean;
  isInWebView: boolean;
}) => {
  const { Component, pageProps } = appProps;
  const { user, error, isAuthorized } = useAuth();
  const isWebSocketEnabled = useWebsocketFeature();
  const router = useRouter();
  const conversationId = (router.query.conversationId as Array<string> | undefined)?.[0] ?? null;

  const topicSuffixes = useMemo(() => getTopicSuffixes({ topic: 'renter', conversationId }), [conversationId]);

  const webSocketLogger = useMemo(() => bootstrap.logger.child({ name: 'WebSocketConnection' }), []);
  const webSocketLoggerContext = useMemo(
    () => ({
      isMobileDevice,
      isMobileView,
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
      connectionType:
        typeof navigator !== 'undefined' && 'connection' in navigator
          ? (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType
          : 'unknown',
    }),
    [isMobileDevice, isMobileView],
  );

  const paths = pathsManifest(StringConstants.BASE_PATH, brandName);
  const getLayout = Component.getLayout || ((page) => page);

  if (error) {
    return <Custom500 brand={brandName} paths={paths} />;
  }

  if (!user) {
    return null;
  }

  // Determine if user is an impersonator
  const { impersonatorEmail, impersonatorUserToken, isPostingPathAdmin = false } = user;
  const isImpersonator = (!!impersonatorEmail && !!impersonatorUserToken && isPostingPathAdmin) || false;

  return (
    <>
      <UserAnalytics user={user} brandName={brandName} />
      <AppContextProvider
        value={{
          brand: brandName,
          setBrandName,
          isImpersonator,
          isInWebView,
          isMobileDevice,
          isMobileView,
          paths,
        }}
      >
        <WebSocketProvider
          topicSuffixes={topicSuffixes}
          websocketUrl={getWebSocketUrl(brandName)}
          isAuthorized={isAuthorized}
          isEnabled={isWebSocketEnabled}
          debug={process.env.NEXT_PUBLIC_WEBSOCKET_DEBUG === 'true'}
          logger={webSocketLogger}
          loggerContext={webSocketLoggerContext}
        >
          <ToastProvider placement="bottom-end">
            <CookiesProvider>{getLayout(<Component {...pageProps} brand={brandName} />)}</CookiesProvider>
          </ToastProvider>
        </WebSocketProvider>
      </AppContextProvider>
    </>
  );
};

const MyApp = (appProps: AppPropsWithLayout) => {
  useBootstrap(() => import('../bootstrap'));

  const [isClient, setIsClient] = useState(false);
  const [brandName, setBrandName] = useState<Brand | null>(null);
  const { isMobileView } = useWindowSize();
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [isInWebView, setIsInWebView] = useState(false);

  useEffect(() => {
    setIsClient(true);
    const mobileDetect = new MobileDetect(window.navigator.userAgent || '');
    setIsMobileDevice(Boolean(mobileDetect.mobile()));
    setIsInWebView(getIsNative(window.navigator.userAgent));

    // Detect brand on initial render based on cookie override, window.MULTIBRAND_SUPPORT, or hostname as fallback
    getBrand({
      hostnamePatterns: [
        { brand: 'hotpads', patterns: ['hotpads', 'testpads'] },
        { brand: 'zillow', patterns: ['zillow'] },
      ],
      fallback: isBrand(process.env.NEXT_PUBLIC_BRAND) ? process.env.NEXT_PUBLIC_BRAND : undefined,
    }).inspect(setBrandName);
  }, []);

  return (
    <RpErrorBoundary>
      <Head applicationName="satellite-rachel-web" buildRef={process.env.NEXT_PUBLIC_BUILD_REF} />
      <DefaultQueryProvider
        defaultOptions={{
          queries: {
            staleTime: 20 * 1000,
          },
        }}
      >
        {isClient && !brandName && <ErrorPage errorMessage={ERROR_MESSAGE} errorDetails={ERROR_DETAILS} />}
        {isClient && brandName && (
          <ThemeProvider theme={ThemeConstellation}>
            <Auth.Provider brand={brandName}>
              <AppWithAuth
                {...appProps}
                brandName={brandName}
                setBrandName={setBrandName}
                isMobileDevice={isMobileDevice}
                isMobileView={isMobileView}
                isInWebView={isInWebView}
              />
            </Auth.Provider>
          </ThemeProvider>
        )}
      </DefaultQueryProvider>
    </RpErrorBoundary>
  );
};

export default MyApp;
